import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Search, X } from "lucide-react";

/**
 * Autocomplete for medical code catalogs (ICD-10, SNOMED, LOINC).
 * Free-form typing is allowed; picking a suggestion fills both code + description.
 * Props:
 *  - kind: "icd10" | "snomed" | "loinc"
 *  - value: current code string
 *  - onChange: (code) => void
 *  - onPick: ({code, description}) => void
 *  - placeholder
 *  - testId
 */
export default function CodeAutocomplete({ kind, value, onChange, onPick, placeholder = "Search…", testId, className = "" }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const fetchResults = async (query) => {
    try {
      const r = await api.get(`/medical/${kind}/search`, { params: { q: query, limit: 12 } });
      setResults(r.data || []);
      setActive(0);
    } catch { setResults([]); }
  };
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchResults(q), 150);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line

  const pick = (item) => {
    setQ(item.code);
    setOpen(false);
    onChange?.(item.code);
    onPick?.(item);
  };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      if (results[active]) { e.preventDefault(); pick(results[active]); }
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      <div className="relative">
        <input data-testid={testId} value={q} onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey} placeholder={placeholder}
          className="w-full h-11 rounded-lg border border-[#E5DFD3] bg-white pl-9 pr-8 text-sm text-[#3E2723] outline-none focus:ring-2 focus:ring-[#8D6E63]" />
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
        {q && <button type="button" onClick={() => { setQ(""); onChange?.(""); setOpen(true); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={12} /></button>}
      </div>
      {open && (
        <div data-testid={`${testId}-results`} className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="p-3 text-xs text-[#7B6157]">No matches. You can type a custom code.</div>
          ) : results.map((r, i) => (
            <button key={r.code + r.description} type="button" data-testid={`${testId}-option-${i}`}
              onClick={() => pick(r)}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                i === active ? "bg-[#F5F2EB]" : "hover:bg-[#FDFCF8]"}`}>
              <span className="font-mono text-[12px] font-bold text-[#3E2723] shrink-0 w-20">{r.code}</span>
              <span className="text-[#5D4037]">{r.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
